import type { QueryClient, QueryKey } from '@tanstack/solid-query';
import type { ScheduleBookingDto, ScheduleGridDto } from '~/shared/api/schedule-api';
import { wallClockDate } from '~/shared/lib/wallClock';

export interface GridMove {
  bookingId: string;
  toCourtId: string;
  startAt: string;
  endAt: string;
}

const isGrid = (data: unknown): data is ScheduleGridDto =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray((data as ScheduleGridDto).bookings) &&
  Array.isArray((data as ScheduleGridDto).courts) &&
  typeof (data as ScheduleGridDto).date === 'string';

export function moveBookingInGridCache(queryClient: QueryClient, move: GridMove): QueryKey[] {
  const toDay = wallClockDate(move.startAt);
  const regrouped: QueryKey[] = [];
  for (const [key, data] of queryClient.getQueriesData<unknown>({ queryKey: ['schedule'] })) {
    if (data === undefined) continue;
    if (!isGrid(data)) {
      const bookings = (data as { bookings?: unknown }).bookings;
      if (Array.isArray(bookings) && bookings.some((b) => (b as ScheduleBookingDto)?.id === move.bookingId))
        regrouped.push(key);
      continue;
    }
    const court = data.date === toDay ? data.courts.find((c) => c.id === move.toCourtId) : undefined;
    const index = data.bookings.findIndex((b) => b.id === move.bookingId);
    if (index === -1) {
      if (court) regrouped.push(key);
      continue;
    }
    if (!court) {
      regrouped.push(key);
      continue;
    }
    const b = data.bookings[index] as ScheduleBookingDto;
    if (b.courtId === move.toCourtId && b.startAt === move.startAt && b.endAt === move.endAt) {
      continue;
    }
    const bookings = data.bookings.slice();
    bookings[index] = {
      ...b,
      courtId: court.id,
      courtName: court.name,
      courtSport: court.sport,
      startAt: move.startAt,
      endAt: move.endAt,
    };
    queryClient.setQueryData<ScheduleGridDto>(key, { ...data, bookings });
  }
  return regrouped;
}

export function applyBookingRescheduled(queryClient: QueryClient, move: GridMove): void {
  for (const queryKey of moveBookingInGridCache(queryClient, move)) {
    void queryClient.invalidateQueries({ queryKey, exact: true });
  }
}
